import { applyKeyframe } from '@noths/polaris-client-styles';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css, keyframes } from 'styled-components';

const { spacing } = themeVariables;

export const productListWrapper = css`
  margin: 0 0 var(${spacing.lg}) 0;
`;

/**
 * The product grid.
 *
 * Mobile-first, and the column count steps on the width of the container the grid is
 * given rather than on the viewport. `<main>` in `ListingsLayout` is the query
 * container. That is not only the layout-agnostic way to write it, it is more correct
 * than the viewport was: the products column is *narrower* at a 1024px viewport than
 * at 768px, because 1024px is where the filter sidebar appears and takes a quarter of
 * the width. A viewport media query cannot see that, which is why the Ribbons grid
 * this replaces never grew past three columns however wide the screen got.
 *
 * Four columns is as far as it goes. `ProductCard` is verified down to 8.75rem, below
 * which its price row clips and its title cuts mid-word, so a fifth column would be
 * card work rather than a change here.
 */
export const productGrid = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  /* One gap on both axes, so the grid reads as an even field of cards. The value comes
     from the layout, which widens it once the filter sidebar is visible; the fallback
     is what the grid uses anywhere the layout has not set it. */
  gap: var(--productGridGap, var(${spacing.xs}));
  /* Side gutters, matching the gap, so cards sit the same distance from the edge of the
     screen as they do from each other rather than running right up to it. The layout
     zeroes this where the page supplies gutters of its own; the fallback is what the
     grid uses anywhere it has not. No container query could work this out for itself:
     the products column gets *narrower* at the width where the page gutters arrive,
     because they are what it loses, so the widths either side of that overlap. */
  padding-inline: var(--productGridPaddingInline, var(${spacing.xs}));

  @container (min-width: 40rem) {
    grid-template-columns: repeat(3, 1fr);
  }

  @container (min-width: 56rem) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const productCardGridItem = css`
  /* A grid item is as wide as its column, not as wide as its content, so a long
     unbroken word in a title cannot stretch the grid. */
  min-width: 0;
`;

const fadeInAnimation = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

export const fadeIn = css`
  animation: ${applyKeyframe(fadeInAnimation)} 400ms linear;
`;

export const inListingCalloutWrapper = css`
  /* Full width whatever the column count is. Where it falls in the list comes from
     where it sits in the DOM, not from a grid row. */
  grid-column: 1 / -1;
`;
